import Link from "next/link";
import type { ReactNode } from "react";

import { FINDER_COPY, checkLabel, checkingLine, cityHelp, panelHeading } from "./_date-finder.copy";
import type { DateFinderProps, FinderEntry, JanmaOption } from "./_date-finder.model";

/**
 * The /muhurat/<event>/ date finder's markup, without state or data access.
 * The SSR'd shell (MuhuratDateFinder.lazy.tsx) renders the controls and the
 * compact date list from the server's props; the lazy island
 * (MuhuratDateFinder.tsx) renders the same DateRows with its Tara chips and
 * city panels, so the shell and the island's first frame are the same list
 * (review correction 3, option (a): the server <ol> above stays the plain
 * answer, and the finder owns its own compact list of the same dates).
 */

const JANMA_SELECT_ID = "muhurat-janma-nakshatra";

export function checkButtonId(iso: string): string {
  return `muhurat-check-${iso}`;
}

function panelId(iso: string): string {
  return `muhurat-city-panel-${iso}`;
}

/** The finder's controls. Rendered by the boundary only, so the birth-star
 * select is one DOM node for the page's lifetime: an open picker is never
 * swapped away when the island arrives. */
export function FinderControls({
  options,
  janma,
  onJanmaChange,
  onJanmaIntent,
  cityName,
  cityControl,
}: {
  options: readonly JanmaOption[];
  janma: number | null;
  onJanmaChange: (janma: number | null) => void;
  /** A press or focus on the select: time to fetch the island. */
  onJanmaIntent: () => void;
  cityName: string;
  cityControl: ReactNode;
}) {
  return (
    <>
      <h3 className="font-serif text-body font-bold text-text-primary">{FINDER_COPY.heading}</h3>
      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label
            htmlFor={JANMA_SELECT_ID}
            className="block text-caption font-semibold text-text-primary"
          >
            {FINDER_COPY.janmaLabel}
          </label>
          <select
            id={JANMA_SELECT_ID}
            value={janma ?? ""}
            onPointerDown={onJanmaIntent}
            onFocus={onJanmaIntent}
            onChange={(event) =>
              onJanmaChange(event.target.value ? Number(event.target.value) : null)
            }
            className="mt-1 w-full rounded-lg border border-border-default bg-surface-raised px-3 py-2 text-body-small text-text-primary"
          >
            <option value="">{FINDER_COPY.janmaPlaceholder}</option>
            {options.map((option) => (
              <option key={option.number} value={option.number}>
                {option.en} ({option.hi})
              </option>
            ))}
          </select>
          <p className="mt-2 text-caption leading-relaxed text-text-secondary">
            {FINDER_COPY.janmaHelpLead}{" "}
            <Link href="/nakshatra-calculator/" className="text-intent-brand underline">
              {FINDER_COPY.janmaHelpLink}
            </Link>{" "}
            {FINDER_COPY.janmaHelpTail}{" "}
            <Link href="/tara-balam/" className="text-intent-brand underline">
              {FINDER_COPY.taraLink}
            </Link>
          </p>
        </div>
        <div className="text-center sm:text-left">
          <span className="block text-caption font-semibold text-text-primary">
            {FINDER_COPY.cityLabel}
          </span>
          {cityControl}
          <p className="mt-2 text-caption leading-relaxed text-text-secondary">
            {cityHelp(cityName)}
          </p>
        </div>
      </div>
    </>
  );
}

/** The shell's stand-in for TimingCitySelector: the same box, no search code. */
export function ShellCityButton({ name, onPress }: { name: string; onPress?: () => void }) {
  return (
    <div className="relative mt-3 inline-block">
      <button
        type="button"
        onClick={onPress}
        className="rounded-lg border border-border-default bg-surface-raised px-3 py-1.5 text-body-small font-medium text-intent-brand"
      >
        {name}
        <svg
          aria-hidden="true"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="-mt-0.5 ml-1.5 inline-block"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
    </div>
  );
}

export type DateRowsProps = {
  finder: DateFinderProps;
  /** The city each date's day check uses. */
  cityName: string;
  /** The date whose city panel is open. */
  openIso: string | null;
  onCheck: (iso: string) => void;
  /** The open panel's result or error; null while the check runs. */
  panel?: ReactNode;
  /** Island only: the Tara chips under a date. */
  rowExtra?: (entry: FinderEntry, index: number) => ReactNode;
  /** Island only: dates whose every window forms a Tara to avoid (dimmed, never removed). */
  dimmed?: ReadonlySet<string>;
};

/** The finder's compact list of the next listed dates. */
export function DateRows({
  finder,
  cityName,
  openIso,
  onCheck,
  panel = null,
  rowExtra,
  dimmed,
}: DateRowsProps) {
  return (
    <ol className="mt-4 space-y-2" aria-label={`${finder.eventName} dates to check`}>
      {finder.entries.slice(0, finder.visible).map((entry, index) => {
        const open = openIso === entry.iso;
        const dim = dimmed?.has(entry.iso) ?? false;
        return (
          <li
            key={entry.iso}
            data-muhurat-date={entry.iso}
            data-dimmed={dim ? "" : undefined}
            className={`rounded-lg border p-3 ${dim ? "border-border-subtle bg-surface-pressed" : "border-border-default bg-surface-canvas"}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
              <p className="text-body-small text-text-secondary">
                <span
                  className={`font-semibold ${dim ? "text-text-secondary" : "text-text-primary"}`}
                >
                  {entry.date}
                </span>{" "}
                · {entry.windows.map((window) => window.label).join(", ")} IST
              </p>
              <button
                type="button"
                id={checkButtonId(entry.iso)}
                aria-expanded={open}
                aria-controls={open ? panelId(entry.iso) : undefined}
                onClick={() => onCheck(entry.iso)}
                className="rounded-lg border border-border-interactive bg-surface-raised px-3 py-1.5 text-body-small font-semibold text-intent-brand"
              >
                {checkLabel(cityName, open)}
              </button>
            </div>
            {rowExtra?.(entry, index)}
            {open ? (
              <CityPanel id={panelId(entry.iso)} cityName={cityName} date={entry.date}>
                {panel}
              </CityPanel>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The day-check panel opens in the tap's own input event with its full height
 * reserved, so a slow engine reply fills it without moving the page below.
 * The reserve is arithmetic from the token sizes, not a browser measurement.
 * At 360 px wide the panel's text column is 234 px (about 38 caption
 * characters a line): the 2-line heading (36), the sunrise line (20), the
 * three InauspiciousSlots cards stacked (3 x 103 + 2 x 16, + 12), three
 * 3-line notes (3 x 48 + 2 x 8, + 12), the 4-line footnote (64 + 12) and the
 * frame (34) come to 691 px: min-h-174 (696). From 640 px the cards sit in
 * one row and lines hold about 80 characters: 355 px, min-h-90 (360).
 */
function CityPanel({
  id,
  cityName,
  date,
  children,
}: {
  id: string;
  cityName: string;
  date: string;
  children: ReactNode;
}) {
  const heading = panelHeading(cityName, date);
  return (
    <div
      id={id}
      role="region"
      aria-label={heading}
      aria-busy={children ? undefined : true}
      className="mt-3 min-h-174 rounded-lg border border-border-default bg-surface-raised p-4 sm:min-h-90"
    >
      <p className="text-body-small font-semibold text-text-primary">{heading}</p>
      {children ?? (
        <p className="mt-1 text-caption text-text-secondary">{checkingLine(cityName)}</p>
      )}
    </div>
  );
}
